import { useCallback, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAttachmentStore, useModals } from '@renderer/chat'
import { useRegenerateSummary, useMarkSummaryAsRead, useSummaryById, useSummaryDate } from '@renderer/chat/hooks'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer.ts'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer.ts'
import { cleanArtifactTextForCopy } from '../utils/clean-artifact-text'
import { useSummaryContext } from './use-summary-context'

export function useSummaryActions() {
  const { summary, setExpanded, setIsGenerating } = useSummaryContext()
  const { data: fullSummary } = useSummaryById(summary.id)
  const { isToday, dayLabel } = useSummaryDate(summary)
  const regenerateMutation = useRegenerateSummary()
  const markAsReadMutation = useMarkSummaryAsRead()
  const { addAttachment, clearAttachments } = useAttachmentStore()
  const { openModal } = useModals()
  const navigate = useNavigate()
  const location = useLocation()
  const [isCopied, setIsCopied] = useState(false)

  // Sync regeneration state with context to keep popover open
  useEffect(() => {
    setIsGenerating(regenerateMutation.isPending)
  }, [regenerateMutation.isPending, setIsGenerating])

  const onGenerate = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      try {
        await regenerateMutation.mutateAsync()
        setExpanded(true)

        AmplitudeRenderer.trackEvent('Summary Action', {
          type: 'Generate',
          timePeriod: isToday ? 'Today' : 'Yesterday',
          isNew: false,
        })
      } catch (err) {
        console.error(err)
      }
    },
    [regenerateMutation, isToday, setExpanded],
  )

  const onMarkAsRead = useCallback(async () => {
    await markAsReadMutation.mutateAsync(summary.id)
  }, [summary, markAsReadMutation])

  const onStartChat = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      if (fullSummary) {
        clearAttachments()

        const isOnMemorySummariestab = location.pathname.startsWith('/memory/summaries')

        if (isOnMemorySummariestab) {
          navigate('/chat/new')
        }

        // Add the attachment after navigation
        setTimeout(() => {
          const formattedLabel = isToday ? 'Today' : dayLabel

          addAttachment({
            attachment: {
              type: 'dailySummary',
              summary: fullSummary.summary,
              summaryId: summary.id,
            },
            metadata: {
              label: formattedLabel,
              source: 'Summary',
              id: summary.id,
            },
          })

          // Focus the chat input
          const chatInput = document.getElementById('message-input')
          chatInput?.focus()
        }, 100)
      }

      AmplitudeRenderer.trackEvent('Summary Action', {
        type: 'Start Chat',
        timePeriod: isToday ? 'Today' : 'Yesterday',
      })
    },
    [clearAttachments, addAttachment, fullSummary, isToday, navigate],
  )

  const onCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsCopied(true)
      if (fullSummary) {
        const cleanedText = cleanArtifactTextForCopy(fullSummary.summary)
        OverlayIpcRenderer.writeTextToClipboard(cleanedText + '\n\nCaptured by HighlightAI.com')
      }
      setTimeout(() => {
        setIsCopied(false)
      }, 1500)
      AmplitudeRenderer.trackEvent('Summary Action', {
        type: 'Copy',
        timePeriod: dayLabel,
        characters: fullSummary?.summary.length || 0,
        lines: fullSummary?.summary.split('\n').length || 0,
      })
    },
    [fullSummary, dayLabel],
  )

  const onPositiveFeedback = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      openModal('summary-feedback', {
        rating: 'positive',
        summary,
      })
    },
    [openModal, summary],
  )

  const onNegativeFeedback = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      openModal('summary-feedback', {
        rating: 'negative',
        summary,
      })
    },
    [openModal, summary],
  )

  return {
    isGenerating: regenerateMutation.isPending,
    isMarkingAsRead: markAsReadMutation.isPending,
    isCopied,
    onGenerate,
    onMarkAsRead,
    onStartChat,
    onCopy,
    onPositiveFeedback,
    onNegativeFeedback,
    regenerateError: regenerateMutation.error,
    markAsReadError: markAsReadMutation.error,
  }
}
